import { useEffect, useRef } from "react";
import Select from "react-select";
import SelectStyles from "../../common/selectStyles";
import {
  ShipmentType,
  KitOrderValidationType,
} from "../../common/types";
import Validator from "../../common/validation";
import QueryString from "query-string";
import { AppLabels } from "../../common/localization";
import {
  stringTemplate,
  getNumKitsNeeded,
  getNumKitsRemaining,
  getMaxKitsCanUserOrder,
} from "../../common/Helpers";
import { Spinner } from "react-bootstrap";
import Loading from "./../common/Loading";
import Constants from "../../common/constants";
import Card from "../common/Card";
import useHomeMethods from "../../common/hooks";
import PickupLocationSelector from "../common/PickupLocationSelector";
import useHomeV2Methods from "./useHome";
import { ReactComponent as ExternalIcon } from '../../assets/svg/external.svg';

const HomeV2 = () => {
  const doubleCheckWarningRef = useRef<HTMLDivElement | null>(null);
  const successMessageRef = useRef<HTMLDivElement | null>(null);
  const topOfFormRef = useRef<HTMLDivElement | null>(null);

  const params = QueryString.parse(window.location.search);
  const lang = Array.isArray(params.lang)
    ? params.lang[0].toLocaleLowerCase()
    : (params.lang || "en").toLocaleLowerCase();
  const userId = Array.isArray(params.userID)
    ? params.userID[0]
    : params.userID || undefined;
  AppLabels.setLanguage(lang);
  const isPostMessage= (Array.isArray(params.isPostMessage)
    ? params.isPostMessage[0] === "true"
    : params.isPostMessage === "true" || false);
  const postMessageReferrer = (Array.isArray(params.postMessageReferrer)
    ? params.postMessageReferrer[0]
    : params.postMessageReferrer) ?? ""
  const isValidUser = Array.isArray(params.iswithsixweeksflow)
    ? params.iswithsixweeksflow[0] === "true"
    : params.iswithsixweeksflow === "true" || false
    const defaultKitType = Array.isArray(params.kt)
    ? params.kt[0]
    : params.kt || undefined

  useEffect(() => {
    if (!isPostMessage || postMessageReferrer === "") return;

    const root = document.getElementById("root");
    if (!root) return;

    let lastHeight = 0;
    let scheduled = false;
    const sendHeight = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const height = root.scrollHeight;
        if (height === lastHeight) return;
        lastHeight = height;
        window.parent.postMessage(
          { type: "ImReadyFormHeight", height },
          atob(postMessageReferrer),
        );
      });
    };

    sendHeight();

    const resizeObserver = new ResizeObserver(sendHeight);
    resizeObserver.observe(root);

    const mutationObserver = new MutationObserver(sendHeight);
    mutationObserver.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isPostMessage, postMessageReferrer]);

  const methods = useHomeMethods(lang, userId);
  const {
    hivKitTypeOptions,
    selectedHivKitCode,
    amountNeededOptions,
    handleSuccessMessageClick,
    handleInputChange,
    validateFormSubmission,
    handleSubmit
  } = useHomeV2Methods({ homeMethods: methods, userId, lang, isValidUser, isPostMessage, postMessageReferrer, doubleCheckWarningRef, topOfFormRef, defaultKitType });

  let {
    data,
    setData,
    userConfig,
    isSaving,
    hasSubmitted,
    validated,
    valid,
    serverError,
    generatedTokensJson,
    confirmAddress,
    ready,
    pickupProvince,
    setPickupProvince,
    shippingTypeOptions,
    provinces,
    yesNoOptions,
    availableLocations,
  } = methods;

  
  if (data.UserID === undefined) {
    return (
      <div className="my-3">
        <div className="alert alert-danger">No User ID given.</div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="my-3">
        <Loading label="Loading form..." color="primary"></Loading>
      </div>
    );
  }

  if (hasSubmitted) {
    if (isPostMessage && postMessageReferrer !== "") {
      window.parent.postMessage(
        { type: "ImReadyForm", data: generatedTokensJson },
        atob(postMessageReferrer),
      );
    } else {
      (window as any).ReactNativeWebView?.postMessage(JSON.stringify(generatedTokensJson));
      console.log(`ImReadyForm ${JSON.stringify(generatedTokensJson)}`); // legacy
    }

    return (
      <div className="my-3">
        <div
          ref={successMessageRef}
          onClick={handleSuccessMessageClick}
          dangerouslySetInnerHTML={{
            __html: AppLabels.FormV2Submitted.replace(
              /(<? *script)/gi,
              "illegalscript",
            ),
          }}
        ></div>
      </div>
    );
  }

  let { kitsAvailable, kitsOrdered } = userConfig!;
  const covidKitsAvailable =
    kitsAvailable.some((x) => x.key === Constants.CovidKitCode) &&
    kitsAvailable.find((x) => x.key === Constants.CovidKitCode)!.enabled &&
    kitsAvailable.find((x) => x.key === Constants.CovidKitCode)!
      .maxKitsCanUserOrder > 0;
  const covidKitOrdered =
    covidKitsAvailable &&
    getNumKitsRemaining(kitsOrdered, Constants.CovidKitCode) <= 0;

  const [, pickupWarningPrefix, pickupWarningHref, pickupWarningLinkText, pickupWarningSuffix] =
    AppLabels.PickupInventoryWarning.match(
      /^([\s\S]*?)<a[^>]*href=['"]([^'"]+)['"][^>]*>([\s\S]*?)\s*\{1\}\s*<\/a>([\s\S]*)$/,
    ) ?? [];

  return (
    <form className="screen" noValidate onSubmit={handleSubmit}>
      {data.IsValidUser && (<h1
        dangerouslySetInnerHTML={{
          __html: stringTemplate(AppLabels.Welcome, {
            "1": getMaxKitsCanUserOrder(
              kitsAvailable,
              selectedHivKitCode,
            ),
          }).replace(/(<? *script)/gi, "illegalscript"),
        }}
      ></h1>)}
      <Card>
        {data.IsValidUser ? (
          <ul className="list mb-0">
            <li>{AppLabels.WelcomeListOne}</li>
            <li>{AppLabels.WelcomeListTwo}</li>
            <li>{AppLabels.WelcomeListThree}</li>
            <li>{AppLabels.WelcomeListFour}</li>
            <li>{AppLabels.WelcomeListFive}</li>
          </ul>
        ) : (
          <p
            dangerouslySetInnerHTML={{
              __html: AppLabels.UserNotValid.replace(
                /(<? *script)/gi,
                "illegalscript",
              ),
            }}
          ></p>
        )}
      </Card>

      <Card cardRef={topOfFormRef}>
        {validated && !valid && (
          <div className="mb-3">
            <div className="alert alert-danger">
              {AppLabels.FormInvalid}
            </div>
          </div>
        )}
        <div className="mb-3">
          <label className="mb-1" htmlFor="shipment-type">
            {AppLabels.ShipmentType}
          </label>
          <Select
            inputId="shipment-type"
            styles={SelectStyles}
            options={shippingTypeOptions}
            name="ShipmentType"
            value={shippingTypeOptions.filter(
              (x) => x.value === data.ShipmentType,
            )}
            isSearchable={false}
            onChange={(value: any, event: any) =>
              handleInputChange({
                target: { ...event, value: value.value },
              })
            }
          ></Select>
        </div>

        {hivKitTypeOptions.length > 1 && <div className="mb-3">
          <label className="mb-1" htmlFor="kit-type">
            {AppLabels.KitType}
          </label>
          <Select
            inputId="kit-type"
            styles={SelectStyles}
            options={hivKitTypeOptions}
            name="KitType"
            value={hivKitTypeOptions.find(x => x.value === selectedHivKitCode)}
            isSearchable={false}
            onChange={(value: any) => {
              data.Kits.find(
                (x) => hivKitTypeOptions.map(y => y.value).includes(x.KitTypeCode),
              )!.KitTypeCode = value.value;
              setData({ ...data });
            }}
          ></Select>
        </div>}

        <div>
          <label className="mb-1" htmlFor="num-kits-required">
            {stringTemplate(AppLabels.HowManyKits, {
              "1": getMaxKitsCanUserOrder(
                kitsAvailable,
                selectedHivKitCode,
              ),
            })}
          </label>
          <Select
            inputId="num-kits-required"
            styles={SelectStyles}
            options={amountNeededOptions}
            name="AmountNeeded"
            value={amountNeededOptions.filter(
              (x) =>
                x.value === getNumKitsNeeded(data.Kits, selectedHivKitCode),
            )}
            isSearchable={false}
            onChange={(value: any) => {
              data.Kits.find(
                (x) => x.KitTypeCode === selectedHivKitCode,
              )!.NumNeeded = value.value;
              setData({ ...data });
            }}
          ></Select>
          <div
            className={`text-danger ${
              validated &&
              !Validator.isKitQuantityValid(
                selectedHivKitCode,
                data.Kits,
                userConfig!,
                KitOrderValidationType.NumOrders
              )
                ? ""
                : "d-none"
            }`}
          >
            {stringTemplate(AppLabels.HowManyKitsValidation, {
              "1": getMaxKitsCanUserOrder(
                kitsAvailable,
                selectedHivKitCode,
              ),
            })}
          </div>
        </div>
      </Card>

      {data.ShipmentType === ShipmentType.Pickup && (<PickupLocationSelector
        lang={lang}
        provinces={provinces}
        locations={availableLocations}
        selectedProvince={pickupProvince}
        selectedLocationID={data.LocationID}
        handleProvinceChange={setPickupProvince}
        handleLocationChange={(locationId: number) => {
          let newData = { ...data, LocationID: locationId };
          setData(newData);

          if(validated) {
            validateFormSubmission(newData);
          }
        }}
        validated={validated}
       />
      )}

      {covidKitsAvailable && (
        <Card>
          <label className="mb-1 required" htmlFor="covidKit">
            {AppLabels.CovidKit}
          </label>
          {covidKitOrdered ? (
            <input
              className="form-control"
              value="No"
              type="text"
              disabled={true}
            />
          ) : (
            <Select
              inputId="covidKit"
              styles={SelectStyles}
              options={yesNoOptions}
              name="CovidKit"
              value={yesNoOptions.filter(
                (x) =>
                  x.value ===
                  getNumKitsNeeded(data.Kits, Constants.CovidKitCode),
              )}
              isSearchable={false}
              onChange={(value: any) => {
                data.Kits.find(
                  (x) => x.KitTypeCode === Constants.CovidKitCode,
                )!.NumNeeded = value.value;
                setData({ ...data });
              }}
            ></Select>
          )}
          {covidKitOrdered && (
            <div className="alert alert-warning mt-3">
              {AppLabels.CovidKitOrdered}
            </div>
          )}
        </Card>
      )}

      {data.ShipmentType === ShipmentType.Pickup && (
        <Card>
          <div>
            {pickupWarningPrefix}
            <a style={{ display: 'inline-flex', wordBreak: 'break-word' }} target="_blank" rel="noopener noreferrer" href={pickupWarningHref}>
              {pickupWarningLinkText}
              <ExternalIcon className="ml-1" width={16} height={16} />
            </a>
            {pickupWarningSuffix}
          </div>
        </Card>
      )}

      {data.ShipmentType === ShipmentType.Delivery && (
        <Card>
          <div
            id="double-check-warning"
            ref={doubleCheckWarningRef}
            className={confirmAddress ? "alert alert-warning mb-3" : "mb-3"}
            dangerouslySetInnerHTML={{
              __html: confirmAddress
                ? AppLabels.DoubleCheckWarningConfirm
                : AppLabels.DoubleCheckWarningSubmit,
            }}
          ></div>

          <div
            className="mb-2 form-title"
            dangerouslySetInnerHTML={{ __html: AppLabels.NameTitle }}
          ></div>
          <div className="mb-3 row">
            <div className="col-12 col-md-6">
              <div className="mb-3">
                <label className="mb-1 required" htmlFor="first-name">
                  {AppLabels.FirstName}
                </label>
                <input
                  id="first-name"
                  className={`form-control ${
                    validated && data.FirstName.trim().length <= 0
                      ? "border-danger"
                      : ""
                  }`}
                  name="FirstName"
                  value={data.FirstName}
                  required
                  type="text"
                  placeholder="John"
                  onChange={(event) => handleInputChange(event)}
                />
                <div
                  className={`text-danger ${
                    validated && data.FirstName.trim().length <= 0
                      ? ""
                      : "d-none"
                  }`}
                >
                  {AppLabels.FirstNameValidation}
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div>
                <label className="mb-1 required" htmlFor="last-name">
                  {AppLabels.LastName}
                </label>
                <input
                  id="last-name"
                  className={`form-control ${
                    validated && data.LastName.trim().length <= 0
                      ? "border-danger"
                      : ""
                  }`}
                  name="LastName"
                  value={data.LastName}
                  required
                  type="text"
                  placeholder="Doe"
                  onChange={(event) => handleInputChange(event)}
                />
                <div
                  className={`text-danger ${
                    validated && data.LastName.trim().length <= 0
                      ? ""
                      : "d-none"
                  }`}
                >
                  {AppLabels.LastNameValidation}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-2">
            <div className="form-title">{AppLabels.AddressTitle}</div>
            <ul className="inset-list">
              <li>{AppLabels.AddressSubtitle}</li>
            </ul>
          </div>

          <div className="row">
            <div className="col-12 col-md-6">
              <div className="mb-3">
                <label className="mb-1 required" htmlFor="building-number">
                  {AppLabels.BuildingNumber}
                </label>
                <input
                  id="building-number"
                  className={`form-control ${
                    validated && data.BuildingNumber.trim().length < 1
                      ? "border-danger"
                      : ""
                  }`}
                  name="BuildingNumber"
                  value={data.BuildingNumber}
                  required
                  type="text"
                  placeholder="1"
                  onChange={(event) => handleInputChange(event)}
                />
                <div
                  className={`text-danger ${
                    validated && data.BuildingNumber.trim().length < 1
                      ? ""
                      : "d-none"
                  }`}
                >
                  {AppLabels.BuildingNumberValidation}
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="mb-3">
                <label className="mb-1 required" htmlFor="street-name">
                  {AppLabels.StreetName}
                </label>
                <input
                  id="street-name"
                  className={`form-control ${
                    validated && data.StreetName.trim().length < 3
                      ? "border-danger"
                      : ""
                  }`}
                  name="StreetName"
                  value={data.StreetName}
                  required
                  type="text"
                  placeholder={AppLabels.StreetNamePlaceholder}
                  onChange={(event) => handleInputChange(event)}
                />
                <div
                  className={`text-danger ${
                    validated && data.StreetName.trim().length < 3
                      ? ""
                      : "d-none"
                  }`}
                >
                  {AppLabels.StreetNameValidation}
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-6">
              <div className="mb-3">
                <label className="mb-1" htmlFor="unit">
                  {AppLabels.Unit}
                </label>
                <input
                  id="unit"
                  className="form-control"
                  name="Unit"
                  value={data.Unit}
                  type="text"
                  placeholder="1"
                  onChange={(event) => handleInputChange(event)}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="mb-3">
                <label className="mb-1 required" htmlFor="city">
                  {AppLabels.City}
                </label>
                <input
                  id="city"
                  className={`form-control ${
                    validated && data.City.trim().length < 3
                      ? "border-danger"
                      : ""
                  }`}
                  name="City"
                  value={data.City}
                  required
                  type="text"
                  placeholder={AppLabels.CityPlaceholder}
                  onChange={(event) => handleInputChange(event)}
                />
                <div
                  className={`text-danger ${
                    validated && data.City.trim().length < 3 ? "" : "d-none"
                  }`}
                >
                  {AppLabels.CityValidation}
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-6">
              <div className="mb-3">
                <label className="mb-1 required" htmlFor="province">
                  {AppLabels.Province}
                </label>
                <Select
                  inputId="province"
                  styles={SelectStyles}
                  options={provinces}
                  name="Province"
                  value={provinces.filter(
                    (x) => x.value === data.Province,
                  )}
                  isSearchable={false}
                  onChange={(value: any, event: any) =>
                    handleInputChange({
                      target: { ...event, value: value.value },
                    })
                  }
                ></Select>
                <div
                  className={`text-danger ${
                    validated && data.Province.trim().length < 2
                      ? ""
                      : "d-none"
                  }`}
                >
                  {AppLabels.ProvinceValidation}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="mb-3">
                <label className="mb-1 required" htmlFor="postal-code">
                  {AppLabels.PostalCode}
                </label>
                <input
                  id="postal-code"
                  className={`form-control ${
                    validated && data.PostalCode.trim().length !== 7
                      ? "border-danger"
                      : ""
                  }`}
                  name="PostalCode"
                  value={data.PostalCode}
                  required
                  type="text"
                  placeholder="A1A 1A1"
                  onChange={(event) => handleInputChange(event)}
                />
                <div
                  className={`text-danger ${
                    validated && data.PostalCode.trim().length !== 7
                      ? ""
                      : "d-none"
                  }`}
                >
                  {AppLabels.PostalCodeValidation}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {serverError !== undefined && serverError.trim().length > 0 && (
        <Card>
          <div
            className="alert alert-danger"
            dangerouslySetInnerHTML={{
              __html: serverError.replace(/(<? *script)/gi, "illegalscript"),
            }}
          ></div>
        </Card>
      )}

      <Card>
        {isSaving ? (
          <button
            type="submit"
            disabled
            className="btn btn-outline-primary w-100"
          >
            <Spinner
              animation="border"
              variant="primary"
              className="me-2"
              size="sm"
            />
            {AppLabels.Saving}
          </button>
        ) : (
          <button
            type="submit"
            disabled={!data.IsValidUser}
            className="btn btn-outline-primary w-100"
          >
            {confirmAddress ? AppLabels.Confirm : AppLabels.Submit}
          </button>
        )}
      </Card>
    </form>
  );
}

export default HomeV2